/**
 * summaryProjection — pure-function layer that turns the chart's raw
 * data (`markers`, `entriesByToken`, `notesByToken`) into the row
 * shape consumed by `FindingsSummary`'s scope sections + status banner.
 *
 * Extracted from ToothChartDialog.jsx so the projection can be unit-
 * tested directly (the dialog itself pulls in 1.4kLoC of unrelated
 * dependencies — testing the projection through it is expensive).
 *
 * Tracks three live invariants caught by the bug screenshot that
 * prompted this extraction:
 *
 *   1. PROBLEM line never displays an auto-generated procedure note
 *      (those are procedure prose, not clinical diagnoses).
 *   2. PLAN line falls back to procedure rows in `entries` when the
 *      marker's `linked_procedures` is missing the active sitting.
 *   3. STATUS uses the canonical SittingStatus enum strings — the
 *      live state is `'in_progress'`, not `'execution_started'`.
 */
import {
  CANCELLED_STATUSES,
  DONE_STATUSES,
  IN_PROGRESS_STATUSES,
  PLANNED_STATUSES,
  PLAN_STATUS_PRIORITY,
  SCOPE_META,
  deriveStatus,
  isAutoGeneratedNote,
  tokenToHumanLabel,
  tokenToScope,
} from './summaryHelpers';


/**
 * buildProjection — top-level entry point. Returns:
 *   { sections, counts, totalItems }
 *
 * `sections` is ordered per SCOPE_META.order (FM → Quadrants → Teeth →
 * Surfaces → Gum). Sections with zero items are omitted entirely.
 */
export function buildProjection({ entriesByToken, notesByToken, markers }) {
  // 1. Collect every unique token that has ANY data attached.
  const seenTokens = new Map(); // token → { entries: [], hasMarker }
  const ingest = (source) => {
    if (!source) return;
    for (const [token, list] of source) {
      if (!Array.isArray(list) || list.length === 0) continue;
      if (!seenTokens.has(token)) {
        seenTokens.set(token, { entries: [], hasMarker: false });
      }
      const bucket = seenTokens.get(token);
      const seen = new Set(bucket.entries.map(
        (e) => e?.meta_id || e?.sitting_id
          || `${e?.kind}-${e?.content}-${e?.created_at}`,
      ));
      for (const e of list) {
        const k = e?.meta_id || e?.sitting_id
          || `${e?.kind || ''}-${e?.content || ''}-${e?.created_at || ''}`;
        if (seen.has(k)) continue;
        seen.add(k);
        bucket.entries.push(e);
      }
    }
  };
  ingest(entriesByToken);
  ingest(notesByToken);
  if (markers) {
    for (const tok of markers.keys()) {
      if (!seenTokens.has(tok)) {
        seenTokens.set(tok, { entries: [], hasMarker: true });
      } else {
        seenTokens.get(tok).hasMarker = true;
      }
    }
  }

  // 2. Build a FindingItem per token.
  const items = [];
  for (const [token, bucket] of seenTokens.entries()) {
    const scope = tokenToScope(token);
    if (scope === 'unknown') continue;
    const marker = markers?.get?.(token)
      || (scope === 'tooth' || scope === 'surface' || scope === 'gum'
        ? markers?.get?.(String(token).replace(/^gum:/, '').split('-')[0])
        : null);
    const item = buildFindingItem(token, scope, bucket.entries, marker);
    if (item) items.push(item);
  }

  items.sort((a, b) => a.token.localeCompare(b.token));

  const sections = Object.keys(SCOPE_META)
    .map((scope) => ({
      scope,
      meta: SCOPE_META[scope],
      items: items.filter((it) => it.scope === scope),
    }))
    .filter((s) => s.items.length > 0)
    .sort((a, b) => a.meta.order - b.meta.order);

  const counts = { open: 0, planned: 0, in_progress: 0, done: 0, all: items.length };
  for (const it of items) counts[it.status] = (counts[it.status] || 0) + 1;

  return { sections, counts, totalItems: items.length };
}


/**
 * buildSummary — the SINGLE SOURCE OF TRUTH for the summary's sections +
 * status counts. Every surface that renders the per-tooth summary
 * (desktop `StatusBanner`, mobile `TabletChartSummaryOverlay`) MUST go
 * through this one function so the numbers can never drift between UIs.
 *
 * There is ONE chart — all history, always. The previous Recent/Full mode
 * (`chartMode === 'fresh'`) marker filter + recount was removed; this now
 * always returns the full `buildProjection`. Returns
 * `{ sections, counts, totalItems }`.
 */
export function buildSummary({ entriesByToken, notesByToken, markers }) {
  return buildProjection({ entriesByToken, notesByToken, markers });
}


/**
 * Detect a procedure row (came from `entriesByToken` — populated by
 * sittings in `buildIndices`). These carry `sitting_id` and a sitting
 * status string. Distinct from metadata NOTE rows which carry `meta_id`.
 */
function isProcedureRow(e) {
  return !!(e?.sitting_id);
}


/**
 * Pull every active procedure for the token from BOTH places we know
 * about: `marker.linked_procedures` AND raw procedure rows in `entries`.
 * Dedupe by `sitting_id`. This is the fix for bug 3 — `linked_procedures`
 * sometimes misses an in-progress sitting (e.g. when the underlying note
 * link isn't established yet); the procedure row in `entries` still has
 * the correct status.
 */
function collectLinkedProcedures(entries, marker) {
  const out = new Map(); // sitting_id → procedure link object

  const fromMarker = Array.isArray(marker?.linked_procedures)
    ? marker.linked_procedures : [];
  for (const p of fromMarker) {
    if (!p?.sitting_id) continue;
    out.set(p.sitting_id, {
      name: p.name || marker?.planned_summary || 'Procedure',
      assignee: p.assignee || marker?.planned_assignee || null,
      status: String(p.status || '').toLowerCase(),
      sitting_id: p.sitting_id,
      // Plan/visit linkage — needed by the row-action toolbar so it
      // can dispatch executeSitting / completeSitting / addProcedureNote
      // without an extra fetch. Marker rows from buildIndices don't
      // carry these today, so the entry pass below typically fills them.
      plan_id: p.plan_id || null,
      visit_id: p.visit_id || null,
      // Lifecycle stamps — preserved raw so the popover can show each
      // moment on its own line (Created / Started / Completed / Paid).
      // `startedAt` (executed_at || created_at) is kept for callers that
      // want a single "when did this become active" answer.
      created_at: p.created_at || null,
      executed_at: p.executed_at || null,
      completed_at: p.completed_at || null,
      payment_cleared_at: p.payment_cleared_at || null,
      startedAt: p.executed_at || p.created_at || null,
      finalCost: null,
    });
  }
  for (const e of entries) {
    if (!isProcedureRow(e)) continue;
    const sid = e.sitting_id;
    // Cost may arrive as a Decimal-stringified value; coerce safely.
    const rawCost = e.final_cost != null ? Number(e.final_cost) : null;
    const finalCost = (rawCost != null && !Number.isNaN(rawCost) && rawCost > 0)
      ? rawCost : null;
    const incoming = {
      name: e.procedure_name || e.procedure?.name || 'Procedure',
      assignee: e.assigned_to_name || e.completer_name || e.assignee_name || null,
      status: String(e.status || '').toLowerCase(),
      sitting_id: sid,
      // Identifiers needed for procedure actions (Start / Complete /
      // Note / Cancel) — preserved from the raw sitting row.
      plan_id: e.plan_id || null,
      visit_id: e.visit_id || null,
      // Lifecycle stamps — keep each raw so the popover can render
      // Created / Started / Completed / Paid as discrete rows. The
      // legacy `startedAt` (executed_at || created_at) is preserved
      // for the chart's PLAN line which only wants the active moment.
      created_at: e.created_at || null,
      executed_at: e.executed_at || null,
      completed_at: e.completed_at || null,
      payment_cleared_at: e.payment_cleared_at || null,
      startedAt: e.executed_at || e.created_at || null,
      finalCost,
      // E — proposed-by doctor + validity window, so a Planned row can show
      // "proposed by Dr X" and an Expired tag when past expires_at.
      origin_doctor_name: e.origin_doctor_name || null,
      expires_at: e.expires_at || null,
      standing: e.standing ?? null,
      cancelled_by_name: e.cancelled_by_name || null,
    };
    if (!out.has(sid)) {
      out.set(sid, incoming);
    } else {
      // Marker version exists. Prefer the entry's status if the marker
      // is stale (e.g. marker still says 'planned' but the live row is
      // 'in_progress'). Highest precedence wins.
      const existing = out.get(sid);
      if ((PLAN_STATUS_PRIORITY[incoming.status] || 0)
          > (PLAN_STATUS_PRIORITY[existing.status] || 0)) {
        out.set(sid, { ...existing, ...incoming });
      } else {
        // Even when status priority loses, lift fields the marker version
        // lacks from the richer entry row. CRITICAL: the marker
        // `linked_procedures` never carry `plan_id`/`visit_id`, so without
        // this the row-action toolbar dispatches to `/plans/null/...` and
        // Extend/Cancel silently fail. The entry row is authoritative for
        // identifiers + lifecycle, so null-fill them here.
        if (incoming.plan_id && !existing.plan_id) {
          existing.plan_id = incoming.plan_id;
        }
        if (incoming.visit_id && !existing.visit_id) {
          existing.visit_id = incoming.visit_id;
        }
        if (incoming.startedAt && !existing.startedAt) {
          existing.startedAt = incoming.startedAt;
        }
        if (incoming.finalCost != null && existing.finalCost == null) {
          existing.finalCost = incoming.finalCost;
        }
        // E-fields the marker version never has (proposed-by / validity /
        // standing / cancelled-by) — keep the Planned row accurate.
        if (incoming.origin_doctor_name && !existing.origin_doctor_name) {
          existing.origin_doctor_name = incoming.origin_doctor_name;
        }
        if (incoming.expires_at && !existing.expires_at) {
          existing.expires_at = incoming.expires_at;
        }
        if (incoming.standing != null && existing.standing == null) {
          existing.standing = incoming.standing;
        }
        if (incoming.cancelled_by_name && !existing.cancelled_by_name) {
          existing.cancelled_by_name = incoming.cancelled_by_name;
        }
      }
    }
  }
  return Array.from(out.values());
}


/**
 * Order procedures by relevance: highest `PLAN_STATUS_PRIORITY` first,
 * then alphabetical name for stable ordering. Cancelled (priority 0) sort
 * last. Shared by the PLAN-line primary pick AND the full `plans` list the
 * View popover renders, so the two can never disagree.
 */
function sortByPlanPriority(procs) {
  return [...(procs || [])].sort((a, b) => {
    const pa = PLAN_STATUS_PRIORITY[a.status] || 0;
    const pb = PLAN_STATUS_PRIORITY[b.status] || 0;
    if (pa !== pb) return pb - pa;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

// Best lifecycle date for a procedure, for chronological ordering.
function procDate(p) {
  return p?.completed_at || p?.executed_at || p?.payment_cleared_at
    || p?.startedAt || p?.created_at || '';
}

// Newest-first by date — the order the View popover lists a tooth's procedures.
function sortByDateDesc(procs) {
  return [...(procs || [])].sort(
    (a, b) => String(procDate(b)).localeCompare(String(procDate(a))),
  );
}


/**
 * Compute STATUS from the collected procedures + raw entries. This
 * supersedes `deriveStatus` for the dialog because we now have the
 * fully-resolved procedure list (including procedure rows from
 * `entries`, which `marker.linked_procedures` may have missed).
 */
function pickStatus(procs, entries, marker) {
  const live = procs.filter((p) => !CANCELLED_STATUSES.has(p.status));
  if (live.length > 0) {
    const anyInProgress = live.some((p) => IN_PROGRESS_STATUSES.has(p.status));
    if (anyInProgress) return 'in_progress';
    const allDone = live.every((p) => DONE_STATUSES.has(p.status));
    if (allDone) return 'done';
    // Plan exists but nothing's started (payment cleared/pending, scheduled,
    // ready, on hold, …) → 'planned', NOT 'in_progress'. This is the fix for
    // teeth that showed "IN PROGRESS" while still on a Start button.
    const anyPlanned = live.some((p) => PLANNED_STATUSES.has(p.status));
    if (anyPlanned) return 'planned';
    // Mixed (e.g. cancelled stripped out, only done left) — treated above.
    // Anything else (unknown status) → fall through to legacy logic.
  }
  return deriveStatus(marker, entries);
}


export function buildFindingItem(token, scope, entries, marker) {
  // Bucket metadata-style entries (meta_id-keyed) into clinical findings.
  // Procedure-derived rows (sitting_id-keyed) are NOT clinical findings
  // and never feed the PROBLEM line — they belong to the PLAN side.
  const clinical = entries.filter((e) => !isProcedureRow(e));
  const humanNotes = clinical.filter((e) => !isAutoGeneratedNote(e));
  const diagnoses = humanNotes.filter((e) => (e?.kind || 'note') === 'diagnosis');
  const realNotes = humanNotes.filter(
    (e) => (e?.kind || 'note') === 'note' && !!e?.content,
  );

  const procs = collectLinkedProcedures(entries, marker);

  // Phantom guard — a token whose ONLY content is auto-generated procedure
  // prose (no diagnosis, no human note, no procedure at all) is NOT a clinical
  // finding. This drops the "open with no diagnosis / not planned yet" rows left
  // behind when a procedure is cancelled (its auto-note lingers). A tooth with a
  // real diagnosis, a human note, or any procedure still shows.
  if (diagnoses.length === 0 && realNotes.length === 0 && procs.length === 0) {
    return null;
  }

  // Row's single PLAN line = the most clinically relevant (highest status).
  const planEntry = sortByPlanPriority(procs)[0] || null;
  // The full list for the View popover is ordered newest-first by date.
  const allPlans = sortByDateDesc(procs);

  // PROBLEM — speaks ONLY about clinical findings. Procedure progress
  // belongs to the PLAN line below; this surface is "what's wrong with
  // the patient?" and the answer is either a diagnosis, a real human
  // note, or — explicitly — that no diagnosis has been recorded yet.
  let problemText = '';
  let problemSeverity = null;
  let problemTone = 'default';
  if (diagnoses.length > 0) {
    const latest = diagnoses[diagnoses.length - 1];
    problemText = latest?.content || 'Diagnosis';
    problemSeverity = latest?.severity || null;
  } else if (realNotes.length > 0) {
    problemText = realNotes[realNotes.length - 1].content;
  } else {
    // No diagnosis AND no human note. Same prompt regardless of whether
    // a procedure has been started (which is now PLAN's job to surface).
    // Amber tone so the missing diagnosis stands out against rows that
    // do have one.
    problemText = 'No diagnosis yet — tap to add';
    problemTone = 'missing-diagnosis';
  }

  let status = pickStatus(procs, clinical, marker);
  // E — a token whose only procedures are cancelled (and has no real diagnosis
  // or human note) buckets as 'cancelled' rather than 'open'. Surfaces under the
  // Cancelled filter; it never colours the tooth (no marker was built).
  if (status === 'open' && diagnoses.length === 0 && realNotes.length === 0) {
    const live = procs.filter(
      (p) => !CANCELLED_STATUSES.has(String(p.status || '').toLowerCase()),
    );
    const anyCancelled = procs.some(
      (p) => CANCELLED_STATUSES.has(String(p.status || '').toLowerCase()),
    );
    if (live.length === 0 && anyCancelled) status = 'cancelled';
  }

  // Newest-activity timestamp for the row. Picks the MAX across every
  // signal we surface so the "updated Xd ago" tag and the Recent-mode
  // sort key both reflect the truly latest touch regardless of whether
  // it came from a procedure event or a diagnosis edit.
  const lastDiagAt = diagnoses[diagnoses.length - 1]?.created_at || '';
  const lastNoteAt = realNotes[realNotes.length - 1]?.created_at || '';
  const planAt = planEntry ? (
    planEntry.completed_at
    || planEntry.executed_at
    || planEntry.payment_cleared_at
    || planEntry.startedAt
    || planEntry.created_at
    || ''
  ) : '';
  const updatedAt = [lastDiagAt, lastNoteAt, planAt]
    .filter(Boolean)
    .reduce((a, b) => (b > a ? b : a), '');

  return {
    token,
    scope,
    label: tokenToHumanLabel(token, {
      compact: scope === 'surface' || scope === 'gum',
    }),
    shortLabel: tokenToHumanLabel(token, { compact: true }),
    problem: problemText,
    problemSeverity,
    problemTone,
    diagnosisCount: diagnoses.length,
    noteCount: realNotes.length,
    updatedAt,
    plan: planEntry
      ? {
        name: planEntry.name,
        assignee: planEntry.assignee,
        status: planEntry.status,
        completed_at: planEntry.completed_at,
        // Lifecycle stamps — passed through raw so the popover can show
        // Created / Started / Completed / Paid as discrete rows.
        created_at: planEntry.created_at || null,
        executed_at: planEntry.executed_at || null,
        payment_cleared_at: planEntry.payment_cleared_at || null,
        // GC — time + cost context surfaced on the PLAN line so the
        // dentist can read "Composite Filling · In progress · started
        // 4m ago · ₹1,200" without opening the procedures panel.
        startedAt: planEntry.startedAt || null,
        finalCost: planEntry.finalCost ?? null,
        // E — proposing doctor + validity window for the Planned row's
        // "proposed by Dr X" + Expired tag.
        originDoctor: planEntry.origin_doctor_name || null,
        expiresAt: planEntry.expires_at || null,
        standing: planEntry.standing ?? null,
        cancelledBy: planEntry.cancelled_by_name || null,
        // H2 — IDs the row-action toolbar needs to dispatch
        // executeSitting / completeSitting / addProcedureNote.
        sittingId: planEntry.sitting_id || null,
        planId: planEntry.plan_id || null,
        visitId: planEntry.visit_id || null,
      }
      : null,
    // Full procedure list for the tooth (sorted same as `plan`), so the View
    // popover can list EVERY procedure, not just the primary + "+N more".
    plans: allPlans.map((p) => ({
      name: p.name,
      assignee: p.assignee,
      status: p.status,
      created_at: p.created_at || null,
      executed_at: p.executed_at || null,
      completed_at: p.completed_at || null,
      payment_cleared_at: p.payment_cleared_at || null,
      startedAt: p.startedAt || null,
      finalCost: p.finalCost ?? null,
      sittingId: p.sitting_id || null,
      planId: p.plan_id || null,
      visitId: p.visit_id || null,
    })),
    status,
    extraPlanCount: Math.max(procs.length - 1, 0),
  };
}
